import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import Swal from "sweetalert2";
import moment from "moment";
import "moment/locale/pt-br";
import { Shell } from "../../common/shell/shell.page";
import { PageHeader } from "../../common/components/page-header";
import { LoadingOverlay } from "../../common/components/loading-overlay";
import application from "../../core/application";
import { Permission } from "../../users/user.entity";
import { payableBillRepository } from "../payable-bill.repository";
import type { PayableBill } from "../payable-bill.entity";
import { CashRegisterReportHero } from "../../cash-register/components/cash-register-report-hero";
import { useCashRegister } from "../../cash-register/cash-register-context";
import { MarkPaidDialog } from "../components/mark-paid-dialog";
import { useModuleAccess } from "../../settings/module-access";
import { ModuleLockedView } from "../../settings/components/module-locked-view";

const money = (n: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n);

type TabKey = "pendentes" | "proximos" | "atrasadas" | "recorrentes" | "finalizadas";

export function PayablesListPage() {
  application.useHasPermission(Permission.ACCOUNTS_PAYABLE);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { session, addWithdrawal } = useCashRegister();

  const [items, setItems] = useState<PayableBill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("pendentes");
  const [search, setSearch] = useState("");
  const [markPaidBill, setMarkPaidBill] = useState<PayableBill | null>(null);
  const moduleAccess = useModuleAccess();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await payableBillRepository.listRecentByUpdated(500);
      setItems(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro ao carregar contas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const today = useMemo(() => moment().startOf("day"), []);
  const weekEnd = useMemo(() => moment().add(7, "days").endOf("day"), []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const match = (b: PayableBill) => {
      if (!q) return true;
      return (
        b.getTitle().toLowerCase().includes(q) ||
        b.getPayeeName().toLowerCase().includes(q)
      );
    };

    return items.filter((b) => {
      if (!match(b)) return false;
      const pending = b.getStatus() === "pending";
      const due = b.getDueDate().clone().startOf("day");
      const isRec = b.getIsRecurring();

      switch (tab) {
        case "pendentes":
          return pending;
        case "proximos":
          return pending && due.valueOf() <= weekEnd.valueOf() && due.valueOf() >= today.valueOf();
        case "atrasadas":
          return pending && due.valueOf() < today.valueOf();
        case "recorrentes":
          return pending && isRec;
        case "finalizadas":
          return b.getStatus() === "paid" || b.getStatus() === "cancelled";
        default:
          return true;
      }
    });
  }, [items, tab, search, today, weekEnd]);

  const counts = useMemo(() => {
    const pending = items.filter((b) => b.getStatus() === "pending");
    return {
      pendentes: pending.length,
      proximos: pending.filter(
        (b) =>
          b.getDueDate().clone().startOf("day").valueOf() <= weekEnd.valueOf() &&
          b.getDueDate().clone().startOf("day").valueOf() >= today.valueOf()
      ).length,
      atrasadas: pending.filter((b) => b.isOverdue()).length,
      recorrentes: pending.filter((b) => b.getIsRecurring()).length,
      finalizadas: items.filter(
        (b) => b.getStatus() === "paid" || b.getStatus() === "cancelled"
      ).length,
    };
  }, [items, today, weekEnd]);

  const onMarkPaid = (b: PayableBill) => {
    setMarkPaidBill(b);
  };

  const handleMarkPaidConfirm = async (registerWithdrawal: boolean) => {
    const b = markPaidBill;
    if (!b) return;
    setMarkPaidBill(null);
    const recurring = b.getIsRecurring() && b.getRecurrence() !== "none";
    try {
      if (recurring) {
        b.registerRecurringPayment();
      } else {
        b.markPaidFully();
      }
      await payableBillRepository.save(b);

      if (registerWithdrawal) {
        await addWithdrawal({
          amount: b.getAmount(),
          reason: b.getTitle(),
          categoryId: "pay_bill",
          categoryLabel: "Pagar conta",
          payableBillId: b.id,
        });
      }

      await load();
    } catch (e) {
      await Swal.fire({
        icon: "error",
        title: "Erro",
        text: e instanceof Error ? e.message : "Falha ao salvar.",
      });
    }
  };

  const onCancel = async (b: PayableBill) => {
    const r = await Swal.fire({
      title: "Cancelar conta",
      text: "Deseja cancelar este lançamento?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Cancelar conta",
      cancelButtonText: "Voltar",
      confirmButtonColor: theme.palette.error.main,
    });
    if (!r.isConfirmed) return;
    try {
      b.cancel();
      await payableBillRepository.save(b);
      await load();
    } catch (e) {
      await Swal.fire({
        icon: "error",
        title: "Erro",
        text: e instanceof Error ? e.message : "Falha ao salvar.",
      });
    }
  };

  const onDelete = async (b: PayableBill) => {
    const r = await Swal.fire({
      title: "Excluir",
      text: "Remover permanentemente este registro?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Excluir",
      confirmButtonColor: theme.palette.error.main,
    });
    if (!r.isConfirmed) return;
    try {
      await payableBillRepository.remove(b);
      await load();
    } catch (e) {
      await Swal.fire({
        icon: "error",
        title: "Erro",
        text: e instanceof Error ? e.message : "Falha ao excluir.",
      });
    }
  };

  if (moduleAccess.loading) {
    return (
      <Shell title="Contas a pagar">
        <LoadingOverlay message="Carregando módulo…" subtle />
      </Shell>
    );
  }

  if (!moduleAccess.isEnabled("accounts_payable")) {
    return (
      <Shell title="Contas a pagar">
        <PageHeader
          title="Contas a pagar"
          description="Gestão financeira de vencimentos e recorrências."
        />
        <ModuleLockedView
          moduleName="Contas a pagar"
          groupId="accounts_payable"
          details="Ative este módulo para registrar contas, vencimentos e acompanhar pendências."
          features={["Cadastro de contas a pagar", "Controle de vencimentos e recorrências", "Histórico de pagamentos quitados", "Alertas de contas atrasadas"]}
        />
      </Shell>
    );
  }

  return (
    <Shell title="Contas a pagar">
      {loading ? <LoadingOverlay message="Carregando…" subtle /> : null}

      <PageHeader
        title="Contas a pagar"
        description="Cadastre fornecedores, vencimentos e recorrências. Acompanhe o que vence, o que atrasou e o histórico quitado."
      />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: "center" }} justifyContent="space-between">
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          component={Link}
          to="/contas-a-pagar/nova"
          sx={{ fontWeight: 800, borderRadius: 2, textTransform: "none", alignSelf: { xs: "stretch", sm: "center" } }}
        >
          Nova conta
        </Button>
        <TextField
          size="small"
          placeholder="Buscar por título ou favorecido…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: { sm: 280 } }}
        />
      </Stack>

      <CashRegisterReportHero
        overline="Financeiro"
        title="Painel de vencimentos"
        chips={
          <>
            <Chip
              size="small"
              label={`${counts.pendentes} pendentes`}
              sx={{
                fontWeight: 600,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            />
            <Chip size="small" variant="outlined" label={`${counts.atrasadas} atrasadas`} color="error" sx={{ fontWeight: 600 }} />
            <Chip size="small" variant="outlined" label={`${counts.proximos} nos 7 dias`} sx={{ fontWeight: 600 }} />
          </>
        }
      />

      {error ? (
        <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as TabKey)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2,
          "& .MuiTab-root": { textTransform: "none", fontWeight: 700 },
        }}
      >
        <Tab value="pendentes" label={`Pendentes (${counts.pendentes})`} />
        <Tab value="proximos" label={`7 dias (${counts.proximos})`} />
        <Tab value="atrasadas" label={`Atrasadas (${counts.atrasadas})`} />
        <Tab value="recorrentes" label={`Recorrentes (${counts.recorrentes})`} />
        <Tab value="finalizadas" label={`Finalizadas (${counts.finalizadas})`} />
      </Tabs>

      {isMobile ? (
        <Stack spacing={1.5}>
          {filtered.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              Nenhum registro nesta visão.
            </Typography>
          ) : (
            filtered.map((b) => {
              const overdue = b.getStatus() === "pending" && b.isOverdue();
              const recLabel =
                b.getRecurrence() === "weekly" ? "Semanal" :
                b.getRecurrence() === "monthly" ? "Mensal" :
                b.getRecurrence() === "yearly" ? "Anual" :
                b.getRecurrence();
              return (
                <Card
                  key={b.id}
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: overdue ? "error.main" : "divider",
                  }}
                >
                  <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={700} noWrap>{b.getTitle()}</Typography>
                        {b.getCategory() ? (
                          <Typography variant="caption" color="text.secondary">
                            {b.getCategory()}
                          </Typography>
                        ) : null}
                      </Box>
                      <Typography fontWeight={800} color={overdue ? "error.main" : "text.primary"} sx={{ whiteSpace: "nowrap" }}>
                        {money(b.getAmount())}
                      </Typography>
                    </Stack>

                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} alignItems="center" sx={{ mt: 1 }}>
                      {b.getPayeeName() ? (
                        <Typography variant="caption" color="text.secondary">{b.getPayeeName()}</Typography>
                      ) : null}
                      <Typography
                        variant="caption"
                        fontWeight={overdue ? 700 : 400}
                        color={overdue ? "error.main" : "text.secondary"}
                      >
                        {b.getDueDate().format("L")} · {b.getDueDate().fromNow()}
                      </Typography>
                      {b.getIsRecurring() ? (
                        <Chip
                          size="small"
                          icon={<EventRepeatOutlinedIcon sx={{ fontSize: 14 }} />}
                          label={recLabel}
                          variant="outlined"
                          sx={{ fontWeight: 600, height: 22 }}
                        />
                      ) : null}
                    </Stack>

                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ mt: 1 }}>
                      {b.getStatus() === "pending" ? (
                        <>
                          <IconButton size="small" color="success" title="Marcar paga" onClick={() => onMarkPaid(b)}>
                            <CheckCircleOutlineIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="warning" title="Cancelar" onClick={() => onCancel(b)}>
                            <CancelOutlinedIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : null}
                      <IconButton size="small" component={Link} to={`/contas-a-pagar/editar/${b.id}`} title="Editar">
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" title="Excluir" onClick={() => onDelete(b)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Stack>
      ) : (
        <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    "& .MuiTableCell-head": { fontWeight: 700, fontSize: 13 },
                  }}
                >
                  <TableCell>Título</TableCell>
                  <TableCell>Favorecido</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell>Vencimento</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                        Nenhum registro nesta visão.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((b) => {
                    const overdue = b.getStatus() === "pending" && b.isOverdue();
                    return (
                      <TableRow key={b.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                        <TableCell>
                          <Typography fontWeight={700}>{b.getTitle()}</Typography>
                          {b.getCategory() ? (
                            <Typography variant="caption" color="text.secondary">
                              {b.getCategory()}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{b.getPayeeName() || "—"}</Typography>
                        </TableCell>
                        <TableCell align="right">{money(b.getAmount())}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={overdue ? 700 : 500}
                            color={overdue ? "error.main" : "text.primary"}
                          >
                            {b.getDueDate().format("L")}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {b.getDueDate().fromNow()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {b.getIsRecurring() ? (
                            <Chip
                              size="small"
                              icon={<EventRepeatOutlinedIcon sx={{ fontSize: 16 }} />}
                              label={
                                b.getRecurrence() === "weekly" ? "Semanal" :
                                b.getRecurrence() === "monthly" ? "Mensal" :
                                b.getRecurrence() === "yearly" ? "Anual" :
                                b.getRecurrence()
                              }
                              variant="outlined"
                              sx={{ fontWeight: 600 }}
                            />
                          ) : (
                            <Chip size="small" label="Única" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {b.getStatus() === "pending" ? (
                              <>
                                <IconButton size="small" color="success" title="Marcar paga" onClick={() => onMarkPaid(b)}>
                                  <CheckCircleOutlineIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="warning" title="Cancelar lançamento" onClick={() => onCancel(b)}>
                                  <CancelOutlinedIcon fontSize="small" />
                                </IconButton>
                              </>
                            ) : null}
                            <IconButton size="small" component={Link} to={`/contas-a-pagar/editar/${b.id}`} title="Editar">
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" title="Excluir" onClick={() => onDelete(b)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Paper variant="outlined" sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.04) }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Dica
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Na criação ou edição, use <strong>Preencher com IA</strong> enviando foto ou PDF do boleto ou
          fatura para extrair valor, vencimento e favorecido automaticamente (Gemini nas Cloud Functions).
        </Typography>
      </Paper>

      <MarkPaidDialog
        open={markPaidBill !== null}
        bill={markPaidBill}
        cashSessionOpen={session !== null}
        onClose={() => setMarkPaidBill(null)}
        onConfirm={(registerWithdrawal) => handleMarkPaidConfirm(registerWithdrawal)}
      />
    </Shell>
  );
}
